import { ReactNode } from "react";
import type { OrderType } from "../OrderTypeSelector/types";

/**
 * What a geo block orders — the same four services as the new-order page.
 * Keywords is the sheet the planner started as; Direct and Ratings plan a
 * fixed set of rows over the days; Reviews brings its own table (`body`).
 */
export type PlannerBlockKind = OrderType;

/**
 * What separates keywords in a pasted list — commas, line breaks (a sheet
 * column) and tabs (a sheet row). The same set App Insights splits on.
 */
export const KEYWORD_LIST_SEPARATOR = /[,\n\t]/;

/** Store of the planned app — drives the "View in <store>" link and glyph. */
export type PlannerStore = "apple" | "google";

/** One date column of the plan. `key` is YYYY-MM-DD, `label` is DD.MM. */
export type PlannerDay = {
  key: string;
  label: string;
  /** Saturday/Sunday — rendered with a subtle column tint. */
  weekend?: boolean;
};

/**
 * The metric columns come from the app's data, so only the keyword and the
 * manager's comment are typed.
 */
export type PlannerRowField = "keyword" | "comment";

/**
 * How the Fill column spreads its number over the row's days: Fixed puts it on
 * every day as is; the Random modes take it as the row's total and split it at
 * random along a trend — growing day by day, or falling.
 */
export type PlannerFillMode = "fixed" | "random-increase" | "random-decrease";

export const PLANNER_FILL_MODES: { value: PlannerFillMode; label: string }[] = [
  { value: "fixed", label: "Fixed" },
  { value: "random-increase", label: "Random-Increase" },
  { value: "random-decrease", label: "Random-Decrease" },
];

/** The row's Fill cell: the mode and the raw number typed next to it. */
export type PlannerFill = {
  mode: PlannerFillMode;
  value: string;
};

export type PlannerRowData = {
  /** Stable row id — also the id passed back through every callback. */
  id: string;
  keyword: string;
  /** Pre-formatted search volume of the keyword ("-" when unknown). */
  asa?: string;
  /** Pre-formatted rank before the order ("Позиция до" in the manager sheet). */
  positionTo?: string;
  /** Free-form note on the keyword; the planner never orders it. */
  comment?: string;
  /** Installs per day: day key → raw input value. */
  values: Record<string, string>;
  /** Fill cell of the row; unset reads as Fixed with no number. */
  fill?: PlannerFill;
  /** Another row of the block has the same keyword — marked until one goes. */
  duplicate?: boolean;
  /**
   * A row the block always has, named instead of typed: "Installs" of a
   * Direct block, "5 stars" / "4 stars" of a Ratings block. It can't be
   * renamed or removed, and its days are open from the start.
   */
  label?: string;
  /** The label is a star rating: drawn with a star. */
  stars?: 4 | 5;
  /** Pre-computed row total, formatted by the host. */
  total: number;
};

export type PlannerBlockData = {
  id: string;
  /** What the block orders; unset reads as Keywords. */
  kind?: PlannerBlockKind;
  /** The block's figures in its head, e.g. "77 ratings · ≈ $38.50". */
  summary?: string;
  /** What a day cell counts, for its label: "Installs" unless given. */
  valueLabel?: string;
  /**
   * The block's own content in place of the day grid — the Reviews table.
   * A block with a body has no day columns.
   */
  body?: ReactNode;
  /** Under the grid, before the footer: Direct's app openings. */
  extra?: ReactNode;
  /** ISO 3166 alpha-2 code, e.g. "AT". */
  country: string;
  countryLabel?: string;
  countryIconUrl?: string;
  rows: PlannerRowData[];
  /**
   * The block's own day columns, when every block runs on its own dates
   * (`renderDates`); the plan's shared `days` otherwise.
   */
  days?: PlannerDay[];
  /** Total installs of the block. */
  total: number;
  /** Per-day column sums, day key → installs. Rendered in the totals row. */
  dayTotals?: Record<string, number>;
  /** Pre-formatted budget, e.g. "$54.46"; omitted renders nothing. */
  budget?: string;
  /**
   * Order type in the footer hint, e.g. "Keyword PRO"; "Keywords" when
   * omitted.
   */
  orderTypeLabel?: string;
  /** "Create order" is busy — button shows a spinner and locks. */
  busy?: boolean;
  /** Blocks "Create order" and shows the reason under the footer. */
  error?: string;
  /**
   * Blocks "Create order" with no line under the grid — the reason is shown
   * elsewhere, e.g. under the geo in its country list.
   */
  createDisabled?: boolean;
  /**
   * An order already came out of this block: the grid stops taking edits and
   * the footer shows `notice` instead of the order controls.
   */
  locked?: boolean;
  /** Footer content replacing the order-type select + "Create order". */
  notice?: ReactNode;
};

export type OrderPlannerProps = {
  /** Title of the picked app; empty renders the "Select app" placeholder. */
  appName: string;
  /** App logo: image URL or a ReactNode. Falls back to an initial placeholder. */
  appIcon?: ReactNode | string;
  appDeveloper?: string;
  appId?: string | number;
  /** Store URL of the app — target of the "View in <store>" link. */
  appUrl?: string;
  store?: PlannerStore;
  /** The plan's shared range — unused once blocks carry their own dates. */
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  days?: PlannerDay[];
  blocks: PlannerBlockData[];
  /** Country select options — { value: "AT", label: "Austria" }. */
  countryOptions: { value: string; label: string }[];
  /** Guardrails mirrored from the XLSX importer (100 keywords / 31 days). */
  maxKeywords?: number;
  maxDays?: number;
  /** Plan switcher and toolbar slots owned by the host. */
  headerSlot?: ReactNode;
  actionsSlot?: ReactNode;
  /**
   * Slots replacing the presentational defaults, the same way the AppInsights
   * card does it: the host injects the live apps dropdown, the store link and
   * a real CountryPicker per block, and the layout stays here.
   */
  appSlot?: ReactNode;
  storeLinkSlot?: ReactNode;
  /** Replace the plain date fields with the app's own date pickers. */
  startDateSlot?: ReactNode;
  endDateSlot?: ReactNode;
  renderCountry?: (block: PlannerBlockData) => ReactNode;
  /**
   * The block's own date pickers, next to its geo. Given, every block runs on
   * its own dates (`block.days`) and the header's shared range goes away.
   */
  renderDates?: (block: PlannerBlockData) => ReactNode;
  /** Order type select of a block, in its footer before "Create order". */
  renderOrderType?: (block: PlannerBlockData) => ReactNode;
  /** Read-only rendering: inputs lock, add/remove controls disappear. */
  readOnly?: boolean;
  /** Row whose daily installs are copied — marked until the copy is dropped. */
  copiedRowId?: string;
  /** Installs are copied: every other editable row offers "Paste installs". */
  canPasteRow?: boolean;

  /** Click on the app trigger — the host opens its apps dropdown. */
  onAppClick?: () => void;
  onViewInStore?: () => void;
  onStartDateChange?: (value: string) => void;
  onEndDateChange?: (value: string) => void;

  onBlockAdd?: () => void;
  /**
   * The order types a block can be. Two and more put the new-order page's
   * type cards on every block that is not ordered yet.
   */
  blockKinds?: PlannerBlockKind[];
  /** A block's type card was picked: the block becomes that type. */
  onBlockKindChange?: (blockId: string, kind: PlannerBlockKind) => void;
  onBlockRemove?: (blockId: string) => void;
  onBlockCountryChange?: (blockId: string, country: string) => void;
  /** Hands the block over to the keywords order flow. */
  onCreateOrder?: (blockId: string) => void;

  onRowAdd?: (blockId: string) => void;
  onRowRemove?: (blockId: string, rowId: string) => void;
  /** Copies the row's daily installs; the copied row again drops the copy. */
  onRowCopy?: (blockId: string, rowId: string) => void;
  /** Writes the copied daily installs over this row's. */
  onRowPaste?: (blockId: string, rowId: string) => void;
  onRowFieldChange?: (
    blockId: string,
    rowId: string,
    field: PlannerRowField,
    value: string,
  ) => void;
  /**
   * The manager is done typing a keyword (blur or Enter). Typing fires
   * `onRowFieldChange` on every character, which is too eager for anything
   * that touches the account — this is the commit. `false` says the keyword
   * didn't stay (the block already has it), so Enter keeps the focus on the row
   * instead of moving down.
   */
  onRowKeywordCommit?: (
    blockId: string,
    rowId: string,
    value: string,
  ) => boolean | void;
  /**
   * A keyword list landed in a keyword cell — pasted, or typed with commas and
   * committed. The host turns it into one row per keyword; `replace` says the
   * cell's own text was empty or wholly replaced, so the first keyword may
   * take this row.
   */
  onRowKeywordsPaste?: (
    blockId: string,
    rowId: string,
    text: string,
    replace: boolean,
  ) => void;
  onCellChange?: (
    blockId: string,
    rowId: string,
    dayKey: string,
    value: string,
  ) => void;
  /**
   * The Fill cell changed — its mode or its number — and the host refills the
   * row's days. Shuffle in a Random mode sends the same fill again for a new
   * draw.
   */
  onRowFillChange?: (blockId: string, rowId: string, fill: PlannerFill) => void;

  className?: string;
};
